import AddCircleIcon from '@mui/icons-material/AddCircle';
import CachedIcon from '@mui/icons-material/Cached';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import { DataGridPro, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { dataGridStyles } from '../XDataGrid/common';
import { Loadable } from '../loadable';
import { convertDateToLocal, parseDate } from '../../util/date';

export enum NoteType {
  Contract = 1,
  Rule = 2,
}

export interface NoteItem {
  id: number;
  type: NoteType;
  note: string;
  createdAt?: string;
  createdBy?: string;
}

interface EditableRow {
  id: number | string;
  type: NoteType;
  note: string;
  createdAt?: string;
  createdBy: string;
  isNew: boolean;
  isEditing?: boolean;
}

interface SavePayload {
  creates?: Array<{ type: NoteType; note: string; entityId: number}>;
}

interface NotesDialogProps {
  open: boolean;
  title: string;
  noteType: NoteType;
  notes: NoteItem[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  loading?: boolean;
  saving?: boolean;
  exporting?: boolean;
  entityId: number;
  onClose: () => void;
  onRefresh: () => void;
  onPageChange: (nextPage: number) => void;
  sortModel: GridSortModel;
  onSortModelChange: (nextSortModel: GridSortModel) => void;
  onSaveInline: (payload: SavePayload) => void;
  onExport: (payload: { scope: 'selected' | 'page' | 'all'; ids?: number[]; page?: number; pageSize?: number; entityName?: string }) => void | Promise<void>;
}

const NEW_ROW_PREFIX = 'new-note-';

const toEditableRows = (notes: NoteItem[] = []): EditableRow[] =>
  notes.map((item) => ({
    id: item.id,
    type: item.type,
    note: item.note ?? '',
    createdAt: item.createdAt ?? '',
    createdBy: item.createdBy ?? '',
    isNew: false,
    isEditing: false,
    isDirty: false,
  }));

function NotesDialog({
  open,
  title,
  noteType,
  notes,
  totalCount,
  page,
  rowsPerPage,
  loading = false,
  saving = false,
  exporting = false,
  entityId,
  onClose,
  onRefresh,
  onPageChange,
  sortModel,
  onSortModelChange,
  onSaveInline,
  onExport,
}: NotesDialogProps) {
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState<'page' | 'all'>('page');
  const [draftNote, setDraftNote] = useState('');

  useEffect(() => {
    if (open) {
      setRows(toEditableRows(notes));
    }
  }, [notes, open]);

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setExportDialogOpen(false);
      setExportScope('page');
    }
  }, [open, page, noteType]);

  const handleAddInline = () => {
    const hasActiveNewRow = rows.some((row) => row.isNew);
    if (hasActiveNewRow) return;

    const nextRow: EditableRow = {
      id: `${NEW_ROW_PREFIX}${Date.now()}`,
      type: noteType,
      note: '',
      createdBy: 'You',
      isNew: true,
      isEditing: true,
    };
    setDraftNote(''); // reset draft
    setRows((prev) => [nextRow, ...prev]);
  };

  const handleNoteChange = (id: number | string, nextValue: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, note: nextValue } : row
      )
    );
  };

  const removeNewRow = (id: number | string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const saveNewRow = (id: number | string) => {
    const targetRow = rows.find((row) => row.id === id);
    if (!targetRow || draftNote.trim().length === 0) return;
    if (!targetRow.isNew) return;

    const payload = {
      creates: [{ type: targetRow.type, note: draftNote.trim(), entityId }],
    } satisfies SavePayload;
    onSaveInline(payload);
  };

  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const allCurrentRowsSelected = rows.length > 0 && selectedRows.length === rows.length;

  const handleExport = async () => {
    if (selectedRows.length === 0) {
      return;
    }

    if (!allCurrentRowsSelected) {
      const selectedNumericIds = selectedRows
        .map((row) => row.id)
        .filter((id): id is number => typeof id === 'number');

      if (selectedNumericIds.length === 0) {
        return;
      }

      await onExport({ scope: 'selected', ids: selectedNumericIds, entityName: title });
      return;
    }

    if (totalCount > rowsPerPage) {
      setExportScope('page');
      setExportDialogOpen(true);
      return;
    }

    await onExport({ scope: 'page', page: page, pageSize: rowsPerPage, entityName: title });
  };

  const handleExportConfirm = async () => {
    if (exportScope === 'all') {
      await onExport({ scope: 'all', entityName: title });
    } else {
      await onExport({ scope: 'page', page: page, pageSize: rowsPerPage, entityName: title });
    }
    setExportDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'createdBy',
      headerName: 'Name',
      flex: 0.35,
      minWidth: 160,
      sortable: true,
      renderCell: (params) => <Typography variant="body2">{params.row.createdBy || '-'}</Typography>,
    },
    {
      field: 'note',
      headerName: 'Notes',
      flex: 0.65,
      minWidth: 360,
      sortable: false,
      cellClassName: 'notes-cell',
      renderCell: (params) => {
        const row = params.row as EditableRow;
        if (row.isNew && row.isEditing) {
          return (
            <Box sx={{ width: '100%', py: 1, opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
              <TextField
                value={draftNote} 
                onChange={(e) => setDraftNote(e.target.value)}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
                fullWidth
                multiline
                minRows={1}
                variant="outlined"
                size="small"
                autoFocus
                placeholder="Enter note"
                disabled={saving}
              />
              <Box display="flex" justifyContent="flex-end" gap={0.5} mt={0.75}>
                {
                  saving ? (
                    <CircularProgress size={18} /> 
                  ) : (
                      <>
                        <Tooltip title="Save note">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => saveNewRow(row.id)}
                              disabled={saving || draftNote.trim().length === 0}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Discard note">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeNewRow(row.id)}
                              disabled={saving}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )
                }
              </Box>
            </Box>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              py: 0.5,
            }}
          >
            {row.note}
          </Typography>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      type: 'dateTime',
      flex: 0.35,
      minWidth: 160,
      sortable: true,
      valueGetter: (value) => parseDate(value, 'date-time'),
      renderCell: (params) => (
            <Typography variant="body2">
              {typeof params.row.createdAt === 'string' && params.row.createdAt
                ? convertDateToLocal(params.row.createdAt)
                : '-'}
            </Typography>
          ),
  },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Tooltip title="Add">
            <span>
              <IconButton size="small" onClick={handleAddInline} disabled={loading || saving}>
                <AddCircleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Export">
            <span>
              <IconButton size="small" onClick={handleExport} disabled={loading || exporting || selectedRows.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={loading || saving}>
                <CachedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Loadable loading={loading}>
        <Box sx={{ height: 430, width: '100%' }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(next) => setSelectedIds(next)}
            getRowHeight={() => 'auto'}
            pagination
            paginationMode="server"
            sortingMode="server"
            sortingOrder={['desc', 'asc']}
            paginationModel={{ page, pageSize: rowsPerPage }}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            onPaginationModelChange={(next) => {
              if (next.page !== page) {
                onPageChange(next.page);
              }
            }}
            pageSizeOptions={[rowsPerPage]}
            rowCount={totalCount || 0}
            localeText={{ noRowsLabel: 'No notes found.' }}
            sx={{
              ...dataGridStyles,
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
                display: 'flex',
              },
              '& .MuiDataGrid-cellContent': {
                whiteSpace: 'normal',
                lineHeight: 1.4,
              },
              '& .notes-cell': {
                alignItems: 'center',
                py: 0.5,
              },
              '& .MuiDataGrid-row': {
                alignItems: 'stretch',
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus::after, & .MuiDataGrid-cell:focus-within::after': {
                display: 'none',
              },
              '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
                boxShadow: 'none',
                backgroundColor: 'transparent',
              },
            }}
          />
        </Box>
        </Loadable>
      </DialogContent>
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Export Notes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            All notes on this page are selected. Choose what you want to export.
          </DialogContentText>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={exportScope}
              onChange={(event) => setExportScope(event.target.value as 'page' | 'all')}
            >
              <FormControlLabel
                value="page"
                control={<Radio size="small" />}
                label={`Export ${rows.length} notes`}
              />
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label={`Export all ${totalCount} notes`}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExportConfirm} variant="contained" color="primary" disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default NotesDialog;
